import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { PhotoIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setHasImage, setImageHasChanged, setUser } from "../../app/userSlice";
import Avatar from "../../components/Avatar";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";
import { useGetSelfMutation } from "../users/usersApiSlice";
import {
	MyAccountViewModel,
	useGetAccountInfoQuery,
	useSaveAccountInfoMutation,
	useUploadAvatarMutation,
	useRemoveAvatarMutation,
} from "./accountApiSlice";

export default function MyAccount() {
	const {
		username,
		userId,
		hasImage,
		course,
		school,
		district,
		isTeachersWriterAccount,
		requiresAccountConfirmation,
	} = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const result = useGetAccountInfoQuery();
	const accountInfoData = result.data;

	const [updateAccountInfo] = useSaveAccountInfoMutation();
	const [updateUserImage] = useUploadAvatarMutation();
	const [removeUserImage] = useRemoveAvatarMutation();
	const [callGetSelf] = useGetSelfMutation();

	const [accountSaveError, setAccountSaveError] = useState<string>();
	const [isPasswordRequired, setIsPasswordRequired] = useState<boolean>();
	const [firstName, setFirstName] = useState<string>();
	const [lastName, setLastName] = useState<string>();
	const [email, setEmail] = useState<string>();
	const [currentPassword, setCurrentPassword] = useState<string>();
	const [newPassword, setNewPassword] = useState<string>();
	const [confirmPassword, setConfirmPassword] = useState<string>();
	const [userImage, setUserImage] = useState<FileList | null>();

	useEffect(() => {
		handleAvatarChange();
	}, [userImage]);

	useEffect(() => {
		setIsPasswordRequired(accountInfoData?.isPasswordRequired);
		setFirstName(accountInfoData?.fName);
		setLastName(accountInfoData?.lName);
		setEmail(accountInfoData?.emailAddress);
	}, [accountInfoData]);

	function handleCancel() {
		accountInfoData?.isPasswordRequired || requiresAccountConfirmation
			? navigate(`/logout`)
			: course
				? navigate(`/home`)
				: navigate(`/courses`);
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = GetUpdateModel();

		if (model) {
			updateAccountInfo(model)
				.unwrap()
				.then(() => {
					callGetSelf()
						.unwrap()
						.then((payload) => {
							dispatch(setUser({ ...payload }));
							course ? navigate(`/home`) : navigate(`/courses`);
						})
						.catch((error: any) => {
							console.log(error);
							course ? navigate(`/home`) : navigate(`/courses`);
						});
				})
				.catch((error) => {
					setAccountSaveError(error);
					scrollTo(0, 0);
					console.log(error);
				});
		}
	}

	function handleAvatarChange() {
		setAccountSaveError("");

		if (userImage) {
			// Use formData to upload the user avatar
			const formData = new FormData();
			formData.append("file", userImage[0]);

			updateUserImage(formData)
				.unwrap()
				.then((payload) => {
					dispatch(setHasImage(true));
					dispatch(setImageHasChanged(true));
				})
				.catch((error) => {
					dispatch(setHasImage(false));
					setAccountSaveError(error.data.message);
					scrollTo(0, 0);
				});
		}
	}

	function handleRemoveUserImage() {
		setAccountSaveError("");
		removeUserImage()
			.unwrap()
			.then(() => {
				dispatch(setHasImage(false));
			})
			.catch((error) => {
				setAccountSaveError(error.data.message);
				scrollTo(0, 0);
			});
	}

	function GetUpdateModel(): MyAccountViewModel | null {
		if (!accountInfoData) return null;
		const model: MyAccountViewModel = {
			...accountInfoData,
			fName: firstName ?? "",
			lName: lastName ?? "",
			emailAddress: email,
			currentPassword: currentPassword,
			newPassword: newPassword,
			confirmPassword: confirmPassword,
		};
		return model;
	}

	return (
		<form>
			<div className="space-y-12">
				{isPasswordRequired && (
					<AlertMessage
						className="mt-4"
						type={AlertLevel.Warning}
						message="Please confirm your account information is correct. You must change your password. Click Save when complete. If there is an error in your account information and editing is enabled, edit fields as required and save. If editing is disabled, please call or write to the help desk for assistance."
						showIcon={true}
					/>
				)}
				<AlertMessage
					className="mt-8"
					type={AlertLevel.Error}
					message={accountSaveError}
					showIcon={true}
					onClear={() => setAccountSaveError(undefined)}
				/>
				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7 text-gray-900">Account Information</h2>
						{(accountInfoData?.canEditName || accountInfoData?.isEmailAvailable) && (
							<p className="mt-1 text-sm leading-6 text-gray-600">
								<RequiredField /> - Denotes a required field
							</p>
						)}
					</div>

					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						<div className="text-l sm:col-span-4">
							<label
								htmlFor="username"
								className="block font-medium leading-6 text-gray-900">
								Username
							</label>
							<div className="mt-2">
								<div className="read-only:bg-slate-200/75 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
									<input
										type="text"
										name="username"
										id="username"
										className="block flex-1 rounded-md placeholder:text-gray-500 border-0 bg-transparent py-1.5 focus:ring-0 sm:text-sm sm:leading-6"
										placeholder={username ? username : ""}
										readOnly
									/>
								</div>
							</div>
						</div>

						<div className="sm:col-span-3">
							<label
								htmlFor="district-name"
								className="block font-medium leading-6 text-gray-900">
								District
							</label>
							<div className="mt-2">
								<input
									type="text"
									name="district-name"
									id="district-name"
									className="block w-full read-only:bg-slate-200/75 placeholder:text-gray-500 rounded-md border-0 bg-transparent py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
									placeholder={district ? district.name : "No district"}
									readOnly
								/>
							</div>
						</div>

						<div className="sm:col-span-3">
							<label
								htmlFor="school-name"
								className="block font-medium leading-6 text-gray-900">
								School
							</label>
							<div className="mt-2">
								<input
									type="text"
									name="school-name"
									id="school-name"
									className="block w-full read-only:bg-slate-200/75 rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 sm:text-sm sm:leading-6"
									placeholder={school ? school.name : "No school"}
									readOnly
								/>
							</div>
						</div>

						<div className="sm:col-span-3">
							<label
								htmlFor="first-name"
								className="block font-medium leading-6 text-gray-900">
								First Name
								{accountInfoData?.canEditName ? <RequiredField /> : ""}
							</label>
							<div className="mt-2">
								<input
									type="text"
									name="first-name"
									id="first-name"
									className="block w-full read-only:bg-slate-200/75 read-only:text-gray-500 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
									readOnly={!accountInfoData?.canEditName}
									required={accountInfoData?.canEditName}
									maxLength={accountInfoData?.rules.firstNameMaxLength}
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
								/>
							</div>
						</div>

						<div className="sm:col-span-3">
							<label
								htmlFor="last-name"
								className="block font-medium leading-6 text-gray-900">
								Last Name
								{accountInfoData?.canEditName ? <RequiredField /> : ""}
							</label>
							<div className="mt-2">
								<input
									type="text"
									name="last-name"
									id="last-name"
									autoComplete="family-name"
									className="block w-full read-only:bg-slate-200/75 read-only:text-gray-500 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
									readOnly={!accountInfoData?.canEditName}
									required={accountInfoData?.canEditName}
									maxLength={accountInfoData?.rules.lastNameMaxLength}
									value={`${lastName}${isTeachersWriterAccount ? " (Student)" : ""}`}
									onChange={(e) => setLastName(e.target.value)}
								/>
							</div>
						</div>

						{accountInfoData?.isEmailAvailable ? (
							<div className="sm:col-span-4">
								<label
									htmlFor="email"
									className="block font-medium leading-6 text-gray-900">
									Email Address
									<RequiredField />
								</label>
								<div className="mt-2">
									<input
										id="email"
										name="email"
										type="email"
										value={email}
										required
										maxLength={accountInfoData?.rules.emailMaxLength}
										onChange={(e) => setEmail(e.target.value)}
										className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
									/>
								</div>
							</div>
						) : null}

						{config?.enableAvatarImages && (
							<div className="col-span-full">
								<label
									htmlFor="photo"
									className="flex font-medium leading-6 text-gray-900">
									User Image
								</label>
								{accountInfoData?.imageUploadDisabled ? null : (
									<p className="mt-1 text-sm leading-6 text-gray-600">
										Images must be 50x50 in size and be a JPG file.
									</p>
								)}
								<div className="mt-2 flex items-center gap-x-3">
									<div className="relative inline-block">
										<Avatar
											className="h-14 w-14 block"
											hasImage={hasImage ?? false}
											initials={accountInfoData?.initials ?? ""}
											userId={userId ?? 0}
											override={true}
										/>
										{hasImage ? (
											<XCircleIcon
												className="mx-auto h-8 w-8 fill-red-700 stroke-white absolute -bottom-2 -right-2 hover:cursor-pointer"
												onClick={() => handleRemoveUserImage()}
											/>
										) : null}
									</div>

									{accountInfoData?.imageUploadDisabled ? (
										<p className="mt-1 leading-6 text-gray-600">
											Uploading a user image is forbidden.{" "}
										</p>
									) : (
										<button
											type="button"
											disabled={accountInfoData?.imageUploadDisabled}
											className="btn btn-neutral">
											<label className="flex hover:cursor-pointer">
												<PhotoIcon
													className="mx-auto h-6 w-6"
													aria-hidden="true"
												/>
												<span className="flex pl-2 items-center">
													Change Avatar
												</span>
												<input
													className="sr-only"
													type="file"
													multiple={false}
													accept=".jpg, .jpeg"
													onChange={(e) => setUserImage(e.target.files)}
													onClick={(e) => (e.currentTarget.value = "")}
												/>
											</label>
										</button>
									)}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7 text-gray-900">Change Your Password</h2>
						{isPasswordRequired ? (
							<p className="mt-1 text-sm leading-6 text-gray-600">
								<RequiredField /> - Denotes a required field
							</p>
						) : (
							<p className="mt-1 mr-24 text-sm leading-6 text-gray-600">
								You must provide your current password to change your password.
							</p>
						)}
						<p className="mt-1 text-sm leading-6 text-gray-600">
							Your new password must be at least{" "}
							{accountInfoData?.minimumPasswordLength} characters long.
						</p>
					</div>

					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						{!isPasswordRequired && (
							<div className="sm:col-span-4">
								<label
									htmlFor="current-password"
									className="block font-medium leading-6 text-gray-900">
									Current Password
								</label>
								<div className="mt-2">
									<Password
										id="current-password"
										name="current-password"
										maxLength={accountInfoData?.rules.passwordMaxLength}
										placeholder="current password"
										className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
										onChange={(e) => setCurrentPassword(e.target.value)}
										value={currentPassword ?? ""}
									/>
								</div>
							</div>
						)}

						<div className="sm:col-span-4">
							<label
								htmlFor="new-password"
								className="block font-medium leading-6 text-gray-900">
								New Password
								{isPasswordRequired ? <RequiredField /> : ""}
							</label>
							<div className="mt-2">
								<Password
									id="new-password"
									name="new-password"
									maxLength={accountInfoData?.rules.passwordMaxLength}
									required={isPasswordRequired}
									placeholder="new password"
									className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
									onChange={(e) => setNewPassword(e.target.value)}
									value={newPassword ?? ""}
								/>
							</div>
						</div>

						<div className="sm:col-span-4">
							<label
								htmlFor="confirm-password"
								className="block font-medium leading-6 text-gray-900">
								Confirm Password
								{isPasswordRequired ? <RequiredField /> : ""}
							</label>
							<div className="mt-2">
								<Password
									id="confirm-password"
									name="confirm-password"
									maxLength={accountInfoData?.rules.passwordMaxLength}
									required={isPasswordRequired}
									placeholder="confirm password"
									className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
									onChange={(e) => setConfirmPassword(e.target.value)}
									value={confirmPassword ?? ""}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="mt-6 flex items-center justify-end gap-x-6">
				<button
					type="submit"
					className="btn btn-primary"
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</button>
				<button type="button" className="btn btn-naked" onClick={handleCancel}>
					Cancel
				</button>
			</div>
		</form>
	);
}
